import { blockElement } from './context'

/** DOM ranges in document order; leaves the native selection unchanged. */
export function reviewChangeRanges(root: HTMLElement | null, blockId: string): { kind: 'add' | 'delete'; range: Range }[] {
  if (!root) return []
  const reviews = [...root.querySelectorAll<HTMLElement>(`[data-block-id="${CSS.escape(blockId)}"], [data-review-id="${CSS.escape(blockId)}"]`)]
  const edits = new Set<HTMLElement>()
  for (const review of reviews) {
    if (['add', 'delete'].includes(review.dataset.diffKind ?? '')) {
      const opposite = review.dataset.diffKind === 'delete' ? review.nextElementSibling : review.previousElementSibling
      if (opposite instanceof HTMLElement && opposite.dataset.diffKind === (review.dataset.diffKind === 'delete' ? 'add' : 'delete')) {
        for (const element of review.dataset.diffKind === 'delete' ? [review, opposite] : [opposite, review]) edits.add(element)
      } else edits.add(review)
    } else {
      const descendants = [...review.querySelectorAll<HTMLElement>('[data-diff-kind="add"], [data-diff-kind="delete"]')]
      for (const element of descendants) edits.add(element)
      if (descendants.length === 0 && ['add', 'delete'].includes(review.dataset.reviewChange ?? '')) edits.add(review)
    }
  }
  return [...edits].map(element => {
    const range = element.ownerDocument.createRange()
    range.selectNodeContents(element)
    return { kind: (element.dataset.diffKind ?? element.dataset.reviewChange) as 'add' | 'delete', range }
  })
}
/** Complete replacement geometry without altering the browser selection. */
export function reviewChangeRect(root: HTMLElement | null, blockId: string): DOMRect | null {
  const ranges = reviewChangeRanges(root, blockId)
  const first = ranges[0]?.range, last = ranges.at(-1)?.range
  if (!first || !last) return null
  const whole = first.cloneRange()
  whole.setEnd(last.endContainer, last.endOffset)
  return whole.getBoundingClientRect()
}
export function clearReviewChangeHighlights(root?: HTMLElement | null) {
  for (const element of root?.querySelectorAll<HTMLElement>('[data-review-focused]') ?? []) delete element.dataset.reviewFocused
  if (root) delete root.dataset.reviewSelectedChange
  globalThis.CSS?.highlights?.delete('review-change-add')
  globalThis.CSS?.highlights?.delete('review-change-delete')
}
export function selectReviewChange(root: HTMLElement | null, blockId: string | null): boolean {
  clearReviewChangeHighlights(root)
  if (root && blockId) root.dataset.reviewSelectedChange = blockId
  root?.dispatchEvent(new CustomEvent(SELECT_CHANGE_EVENT, { detail: { blockId } }))
  if (!blockId) return false
  const element = blockElement(root, blockId)
  if (!element) { if (root) delete root.dataset.reviewSelectedChange; return false }
  const ranges = reviewChangeRanges(root, blockId)
  for (const { range } of ranges) {
    const owner = range.startContainer instanceof HTMLElement ? range.startContainer : range.startContainer.parentElement
    if (owner) owner.dataset.reviewFocused = 'true'
  }
  if (globalThis.CSS?.highlights && typeof Highlight !== 'undefined' && ranges.length > 0) {
    for (const kind of ['add', 'delete'] as const) CSS.highlights.set(`review-change-${kind}`, new Highlight(...ranges.filter(edit => edit.kind === kind).map(edit => edit.range)))
  }
  const target = element.querySelector<HTMLElement>('[data-diff-kind="add"], [data-diff-kind="delete"]') ?? element
  target.ownerDocument.getSelection()?.removeAllRanges()
  root?.dispatchEvent(new CustomEvent(SELECT_COMMENT_EVENT, { detail: { threadId: null, selected: false } }))
  target.scrollIntoView({ block: 'center' })
  return true
}

export const SELECT_CHANGE_EVENT = 'core-review-select-change'
export const SELECT_COMMENT_EVENT = 'core-review-select-comment'
export function selectComment(root: HTMLElement | null, threadId: string): boolean {
  if (!root) return false
  const detail = { threadId, selected: false }
  root.dispatchEvent(new CustomEvent(SELECT_COMMENT_EVENT, { detail }))
  // onReady may run before the overlay's root-dependent layout effect. Select now and
  // let that effect consume the queued card selection when it attaches.
  if (!detail.selected && root.dataset.reviewCommentsReady !== 'true') {
    const mark = root.querySelector<HTMLElement>(`[data-comment-id="${CSS.escape(threadId)}"][data-review-comment-thread-known="true"]`)
    if (!mark) return false
    root.dataset.reviewSelectedComment = threadId
    mark.ownerDocument.getSelection()?.removeAllRanges()
    mark.scrollIntoView({ block: 'center' })
    clearReviewChangeHighlights(root)
    return true
  }
  return detail.selected
}

export const START_BLOCK_COMMENT_EVENT = 'core-review-start-block-comment'
export const START_SELECTION_COMMENT_EVENT = 'core-review-start-selection-comment'
